import { useContext, useEffect, useRef } from 'react'
import { UserContext, UserContextType } from '../UserContext'
import ElectronIPC, { ipc } from '../services/ElectronIPC'
import { IpcRendererEvent } from 'electron'
import useRootSnackbar from './useRootSnackbar'
import { AppContext, AppContextType } from '../AppContext'
import { logger } from './Logger'
import useAudioEngineActions from '../actions/useAudioEngineActions'
import useRoomContextRef from './useRoomContextRef'
import { getAudioEngineInstance, AudioEngineState } from '../core/AudioEngine'
import useUserContextRef from './useUserContextRef'

const log = logger.createChildLogger({ name: 'AudioEngineService' })

type AudioEngineLoaderState = {
    isLoaded: boolean
    error: {
        code: string
        message: string
        debugMessage: string
    } | null
}

export default function useAudioEngineService() {
    const audioEngine = getAudioEngineInstance()
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const snackbar = useRootSnackbar()

    const prevAudioEngineState = useRef<AudioEngineState>(state.audioEngineState)

    const { resyncWithAppState } = useAudioEngineActions()

    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const isFirstInitializationRef = useRef(true)

    useEffect(() => {
        ////
        //// onInitializationSuccess
        ////
        audioEngine.on('onInitializationSuccess', async () => {
            log.info('onInitializationSuccess', 'initialization success.')

            dispatch({
                type: 'update_audio_engine_state',
                value: { isLoaded: true, errorMessage: null },
            })

            if (isFirstInitializationRef.current) {
                isFirstInitializationRef.current = false
            } else {
                resyncWithAppState()
            }
        })

        ////
        //// Handle fatal error
        ////
        audioEngine.on('onFatalError', (err) => {
            log.error('audioEngineOnFatalError', '', { err })

            if (err.code === 'abort') {
                audioEngine.quit()
            }

            if (err.code === 'spawn_failed' || err.code === 'process_exited_retry_limit_exceeded') {
                const msg = `Fatal error occurred in Pro Audio: ${err.code} "${err.debugMessage}".`
                dispatch({
                    type: 'update_audio_engine_state',
                    value: { isLoaded: false, errorMessage: msg },
                })
                appDispatch({
                    type: 'new_message_dialog',
                    value: {
                        id: 'audioEngine::onFatalError',
                        text:
                            'A fatal error occurred in Pro Audio which caused the module to shut down. ' +
                            'Please restart the app to use the Pro Audio features.\n' +
                            'Debug information:\n' +
                            ` - code: "${err.code}"\n` +
                            ` - message: "${err.debugMessage}".\n`,
                    },
                })
            } else if (err.code === 'process_exited_retry') {
                if (roomStateRef.current.isScreenRecording) {
                    // TODO: and if Pro Audio is being sent or received, then we should show a dialog.
                }
            }
        })

        ////
        //// Handle changes in network status
        ////
        let consecErrorCt = 0 // consecutive error ct; gets cleared on CONNECTED.
        audioEngine.on('onNetworkStatusChanged', (ev) => {
            if (ev === 'CONNECTED') {
                if (consecErrorCt > 0) {
                    snackbar.show({ severity: 'success', message: 'Pro Audio connected to the network!' })
                    ElectronIPC.showNotification({
                        title: 'Pro Audio',
                        body: `Connected to the network!`,
                    })
                }

                if (userStateRef.current.audioEngineState?.lastConnectionEvent !== ev) {
                    dispatch({
                        type: 'update_audio_engine_state',
                        value: { lastConnectionEvent: ev },
                    })
                }

                consecErrorCt = 0
            } else if (ev === 'CONNECTING') {
                // no-op
            } else if (ev === 'CONNECTION_FAILED_RETRY' || ev === 'DISCONNECTED_RETRY') {
                if (consecErrorCt === 0) {
                    ElectronIPC.showNotification({
                        title: 'Pro Audio',
                        body: `Pro Audio disconnected from the network. Please wait while we try to reconnect.`,
                    })

                    if (userStateRef.current.audioEngineState?.lastConnectionEvent !== ev) {
                        dispatch({
                            type: 'update_audio_engine_state',
                            value: { lastConnectionEvent: ev },
                        })
                    }
                }
                consecErrorCt++
            } else if (ev === 'PERMANENT_FAILURE') {
                ElectronIPC.showNotification({
                    title: 'Pro Audio network error',
                    body: `Pro Audio network failed to initialize.`,
                })

                if (userStateRef.current.audioEngineState?.lastConnectionEvent !== ev) {
                    dispatch({
                        type: 'update_audio_engine_state',
                        value: { lastConnectionEvent: ev },
                    })
                }
            }
        })

        ////
        //// Handle network stats event
        ////
        audioEngine.on('networkStatsEvent', (stats) => {
            ;(window as any).audioEngineNetworkStats = stats
        })

        ////
        //// _getLoaderState
        //// check state here -- onInitializationSuccess or onFatalError may have been emitted
        //// by the audio engine loader _before_ this effect loaded.
        ////
        ipc.send('AudioEngine._getLoaderState', {})
        ipc.once('AudioEngine._getLoaderState', (_: IpcRendererEvent, data: AudioEngineLoaderState) => {
            const next: Partial<AudioEngineState> = {
                isLoaded: data.isLoaded,
                errorMessage: null,
            }

            if (data.error) {
                next.errorMessage = `${data.error.code}: ${data.error.message} ${data.error.debugMessage}`
            }

            dispatch({
                type: 'update_audio_engine_state',
                value: next,
            })
        })

        return () => {
            audioEngine.removeAllListeners('onInitializationSuccess')
            audioEngine.removeAllListeners('onFatalError')
            audioEngine.removeAllListeners('onNetworkStatusChanged')
            audioEngine.removeAllListeners('networkStatsEvent')
        }
    }, [])

    const audioEngineStateEffectIsFirst = useRef(true)
    useEffect(() => {
        if (audioEngineStateEffectIsFirst.current) {
            audioEngineStateEffectIsFirst.current = false
            return
        }

        if (JSON.stringify(prevAudioEngineState.current) === JSON.stringify(state.audioEngineState)) {
            return
        }

        log.checkpoint('audio_engine_state_changed', '', {
            audioEngineState: state.audioEngineState,
        })

        prevAudioEngineState.current = {
            ...state.audioEngineState,
        }
    }, [state.audioEngineState])
}
